// UsersTab.jsx
import React, { useState, useEffect, useCallback } from 'react';
import userService from '../../../services/userService';

import LoadingState from '../components/LoadingState';
import ErrorState from '../components/ErrorState';
import EmptyState from '../components/EmptyState';

const UsersTab = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [employeesWithoutAccount, setEmployeesWithoutAccount] = useState([]);
  
  // Форма создания пользователя
  const [newUser, setNewUser] = useState({
    employee_id: '',
    username: '',
    role: 'user',
    status: 'active'
  });
  const [showAddUserForm, setShowAddUserForm] = useState(false);
  
  // Фильтры
  const [searchUser, setSearchUser] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  
  // Редактирование
  const [editingUser, setEditingUser] = useState(null);
  const [editForm, setEditForm] = useState({
    fullName: '',
    email: '',
    role: '',
    status: ''
  });
  
  // Смена пароля
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    new_password_confirmation: ''
  });

  // Загрузка пользователей
  const fetchUsers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await userService.getAll();
      setUsers(data);
    } catch (err) {
      console.error('Ошибка загрузки пользователей:', err);
      setError(err.message || 'Не удалось загрузить пользователей');
    } finally {
      setLoading(false);
    }
  }, []);

  // Загрузка сотрудников без учетной записи
  const fetchEmployeesWithoutAccount = useCallback(async () => {
    try {
      const data = await userService.getEmployeesWithoutAccount();
      setEmployeesWithoutAccount(data.employees);
    } catch (err) {
      console.error('Ошибка загрузки сотрудников без аккаунта:', err);
    }
  }, []);

  useEffect(() => {
    fetchUsers();
  }, [fetchUsers]);

  useEffect(() => {
    if (showAddUserForm) {
      fetchEmployeesWithoutAccount();
    }
  }, [showAddUserForm, fetchEmployeesWithoutAccount]);

  // Создание пользователя
  const createUser = async () => {
    if (!newUser.employee_id || !newUser.username) {
      alert('Выберите сотрудника и укажите логин');
      return;
    }

    try {
      setLoading(true);
      const result = await userService.create(newUser);
      
      // Показываем временный пароль
      if (result.temporaryPassword) {
        alert(`Пользователь успешно создан!\n\nВременный пароль: ${result.temporaryPassword}\n\nСообщите его пользователю. При первом входе потребуется смена пароля.`);
      } else {
        alert('Пользователь успешно создан');
      }
      
      // Сброс формы
      setNewUser({ employee_id: '', username: '', role: 'user', status: 'active' });
      setShowAddUserForm(false);
      
      // Обновление списка
      await fetchUsers();
    } catch (err) {
      alert(err.message || 'Ошибка при создании пользователя');
    } finally {
      setLoading(false);
    }
  };

  // Редактирование пользователя
  const startEditing = (user) => {
    setEditingUser(user);
    setEditForm({
      fullName: user.fullName || '',
      email: user.email || '',
      role: user.role || 'user',
      status: user.status || 'active'
    });
  };

  const updateUser = async () => {
    if (!editingUser) return;

    try {
      setLoading(true);
      await userService.update(editingUser.id, editForm);
      setEditingUser(null);
      await fetchUsers();
      alert('Пользователь обновлен');
    } catch (err) {
      alert(err.message || 'Ошибка при обновлении пользователя');
    } finally {
      setLoading(false);
    }
  };

  // Сброс пароля (админ)
  const resetUserPassword = async (userId, userName) => {
    if (!window.confirm(`Сбросить пароль для пользователя "${userName}"?\nБудет сгенерирован новый временный пароль.`)) {
      return;
    }

    try {
      setLoading(true);
      const result = await userService.resetPassword(userId);
      
      if (result.temporaryPassword) {
        alert(`Пароль сброшен!\n\nНовый временный пароль: ${result.temporaryPassword}\n\nПользователь должен сменить его при следующем входе.`);
      } else {
        alert('Пароль успешно сброшен');
      }
    } catch (err) {
      alert(err.message || 'Ошибка при сбросе пароля');
    } finally {
      setLoading(false);
    }
  };

  // Смена пароля текущим пользователем
  const changePassword = async () => {
    if (!passwordForm.current_password || !passwordForm.new_password || !passwordForm.new_password_confirmation) {
      alert('Заполните все поля');
      return;
    }

    if (passwordForm.new_password !== passwordForm.new_password_confirmation) {
      alert('Новый пароль и подтверждение не совпадают');
      return;
    }

    if (passwordForm.new_password.length < 6) {
      alert('Пароль должен содержать минимум 6 символов');
      return;
    }

    try {
      setLoading(true);
      await userService.changePassword(passwordForm);
      setChangingPassword(false);
      setPasswordForm({ current_password: '', new_password: '', new_password_confirmation: '' });
      alert('Пароль успешно изменен');
    } catch (err) {
      alert(err.message || 'Ошибка при смене пароля');
    } finally {
      setLoading(false);
    }
  };

  // Удаление пользователя
  const deleteUser = async (userId, userName) => {
    if (!window.confirm(`Удалить пользователя "${userName}"?\n\nЭто действие необратимо!`)) {
      return;
    }

    try {
      setLoading(true);
      await userService.delete(userId);
      await fetchUsers();
      alert('Пользователь удален');
    } catch (err) {
      alert(err.message || 'Ошибка при удалении пользователя');
    } finally {
      setLoading(false);
    }
  };

  // Блокировка/разблокировка
  const toggleUserStatus = async (userId, currentStatus, userName) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const action = newStatus === 'active' ? 'разблокировать' : 'заблокировать';
    
    if (!window.confirm(`${action.charAt(0).toUpperCase() + action.slice(1)} пользователя "${userName}"?`)) {
      return;
    }

    try {
      setLoading(true);
      await userService.changeStatus(userId, newStatus);
      await fetchUsers();
    } catch (err) {
      alert(err.message || `Ошибка при ${action} пользователя`);
    } finally {
      setLoading(false);
    }
  };

  // Получение названия роли
  const getRoleName = (role) => {
    const roles = {
      'admin': 'Администратор',
      'hr_manager': 'HR-менеджер',
      'training_curator': 'Куратор обучения',
      'user': 'Пользователь'
    };
    return roles[role] || role;
  };

  // Цвет для роли
  const getRoleColor = (role) => {
    const colors = {
      'admin': '#ef4444',
      'hr_manager': '#3b82f6',
      'training_curator': '#10b981',
      'user': '#6b7280'
    };
    return colors[role] || '#6b7280';
  };

  // Фильтрация пользователей
  const filteredUsers = users.filter(user => {
    const searchTerm = searchUser.toLowerCase();
    const matchesSearch = !searchTerm || 
      (user.fullName || '').toLowerCase().includes(searchTerm) ||
      (user.username || '').toLowerCase().includes(searchTerm) ||
      (user.email || '').toLowerCase().includes(searchTerm);
    const matchesRole = roleFilter === 'all' || user.role === roleFilter;
    return matchesSearch && matchesRole;
  });

  // Статистика
  const stats = {
    total: users.length,
    active: users.filter(u => u.status === 'active').length,
    inactive: users.filter(u => u.status === 'inactive').length,
    admins: users.filter(u => u.role === 'admin').length,
    hrManagers: users.filter(u => u.role === 'hr_manager').length,
    curators: users.filter(u => u.role === 'training_curator').length
  };

  if (loading && users.length === 0) {
    return <LoadingState message="Загрузка пользователей..." />;
  }

  if (error && users.length === 0) {
    return <ErrorState error={error} onRetry={fetchUsers} />;
  }

  return (
    <div className="directory-container">
      <div className="directory-header">
        <h2>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '8px', verticalAlign: 'middle' }}>
            <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
            <circle cx="12" cy="7" r="4" />
            <path d="M16 3.13a4 4 0 0 1 0 7.75" />
            <path d="M20 21v-2a4 4 0 0 0-3-3.85" />
          </svg>
          Управление учетными записями
        </h2>
        <div style={{ display: 'flex', gap: '8px' }}>
          <button className="btn-secondary" onClick={() => setChangingPassword(!changingPassword)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
              <path d="M7 11V7a5 5 0 0 1 10 0v4" />
            </svg>
            Сменить пароль
          </button>
          <button className="btn-primary" onClick={() => setShowAddUserForm(!showAddUserForm)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
              <line x1="12" y1="5" x2="12" y2="19" />
              <line x1="5" y1="12" x2="19" y2="12" />
            </svg>
            {showAddUserForm ? 'Отмена' : 'Создать пользователя'}
          </button>
        </div>
      </div>

      {/* Форма смены пароля */}
      {changingPassword && (
        <div style={{ background: '#fef3c7', padding: '20px', borderRadius: '12px', marginBottom: '24px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>Смена пароля</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
            <input
              type="password"
              placeholder="Текущий пароль"
              value={passwordForm.current_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, current_password: e.target.value })}
              style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }}
            />
            <input
              type="password"
              placeholder="Новый пароль"
              value={passwordForm.new_password}
              onChange={(e) => setPasswordForm({ ...passwordForm, new_password: e.target.value })}
              style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }}
            />
            <input
              type="password"
              placeholder="Подтверждение нового пароля"
              value={passwordForm.new_password_confirmation}
              onChange={(e) => setPasswordForm({ ...passwordForm, new_password_confirmation: e.target.value })}
              style={{ padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }}
            />
            <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
              <button onClick={() => {
                setChangingPassword(false);
                setPasswordForm({ current_password: '', new_password: '', new_password_confirmation: '' });
              }} className="btn-secondary">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px' }}>
                  <line x1="18" y1="6" x2="6" y2="18" />
                  <line x1="6" y1="6" x2="18" y2="18" />
                </svg>
                Отмена
              </button>
              <button onClick={changePassword} className="btn-primary">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px' }}>
                  <polyline points="20 6 9 17 4 12" />
                </svg>
                Сменить пароль
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Форма создания пользователя */}
      {showAddUserForm && (
        <div style={{ background: '#f9fafb', padding: '20px', borderRadius: '12px', marginBottom: '24px' }}>
          <h3 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: 600 }}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px', verticalAlign: 'middle' }}>
              <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
              <circle cx="12" cy="7" r="4" />
            </svg>
            Новая учетная запись
          </h3>
          
          {employeesWithoutAccount.length > 0 ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <select
                  value={newUser.employee_id}
                  onChange={(e) => setNewUser({ ...newUser, employee_id: e.target.value })}
                  style={{ flex: 2, padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }}
                >
                  <option value="">Выберите сотрудника</option>
                  {employeesWithoutAccount.map(emp => (
                    <option key={emp.id} value={emp.id}>
                      {emp.name || emp.fullName} - {emp.position || 'Должность не указана'}
                    </option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder="Логин (username)"
                  value={newUser.username}
                  onChange={(e) => setNewUser({ ...newUser, username: e.target.value })}
                  style={{ flex: 1, padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }}
                />
              </div>
              <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                <select
                  value={newUser.role}
                  onChange={(e) => setNewUser({ ...newUser, role: e.target.value })}
                  style={{ flex: 1, padding: '10px 12px', borderRadius: '8px', border: '1px solid #d1d5db', fontSize: '14px' }}
                >
                  <option value="user">Пользователь</option>
                  <option value="training_curator">Куратор обучения</option>
                  <option value="hr_manager">HR-менеджер</option>
                  <option value="admin">Администратор</option>
                </select>
              </div>
              <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px' }}>
                <button onClick={() => setShowAddUserForm(false)} className="btn-secondary">
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px' }}>
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                  Отмена
                </button>
                <button onClick={createUser} className="btn-primary" disabled={!newUser.employee_id || !newUser.username}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '4px' }}>
                    <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                    <circle cx="12" cy="7" r="4" />
                  </svg>
                  Создать пользователя
                </button>
              </div>
            </div>
          ) : (
            <div style={{ padding: '20px', textAlign: 'center', color: '#6b7280' }}>
              Все сотрудники уже имеют учетные записи
            </div>
          )}
        </div>
      )}

      {/* Фильтры */}
      <div className="filters-bar">
        <input
          type="text"
          placeholder="Поиск по ФИО, логину или email..."
          className="search-input"
          value={searchUser}
          onChange={(e) => setSearchUser(e.target.value)}
        />
        <select
          className="filter-select"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
        >
          <option value="all">Все роли</option>
          <option value="admin">Администраторы</option>
          <option value="hr_manager">HR-менеджеры</option>
          <option value="training_curator">Кураторы обучения</option>
          <option value="user">Пользователи</option>
        </select>
        <button 
          className="btn-secondary" 
          onClick={fetchUsers}
        >
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ marginRight: '6px' }}>
            <path d="M21 12a9 9 0 0 1-9 9 9 9 0 0 1 0-18c2.5 0 4.75 1 6.38 2.62" />
            <path d="M21 3v6h-6" />
          </svg>
          Обновить
        </button>
      </div>

      {/* Таблица пользователей */}
      <div className="employees-table-wrapper">
        {filteredUsers.length > 0 ? (
          <table className="directory-table">
            <thead>
              <tr>
                <th>ID</th>
                <th>Логин</th>
                <th>ФИО</th>
                <th>Email</th>
                <th>Роль</th>
                <th>Статус</th>
                <th>Действия</th>
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map(user => (
                <tr key={user.id}>
                  <td>{user.id}</td>
                  <td><strong>{user.username}</strong></td>
                  <td>
                    {editingUser?.id === user.id ? (
                      <input
                        type="text"
                        value={editForm.fullName}
                        onChange={(e) => setEditForm({ ...editForm, fullName: e.target.value })}
                        className="edit-input"
                      />
                    ) : (
                      user.fullName || '—'
                    )}
                  </td>
                  <td>
                    {editingUser?.id === user.id ? (
                      <input
                        type="email"
                        value={editForm.email}
                        onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                        className="edit-input"
                      />
                    ) : (
                      user.email || '—'
                    )}
                  </td>
                  <td>
                    {editingUser?.id === user.id ? (
                      <select
                        value={editForm.role}
                        onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                        className="edit-select"
                      >
                        <option value="user">Пользователь</option>
                        <option value="training_curator">Куратор обучения</option>
                        <option value="hr_manager">HR-менеджер</option>
                        <option value="admin">Администратор</option>
                      </select>
                    ) : (
                      <span style={{
                        display: 'inline-block',
                        padding: '4px 10px',
                        borderRadius: '20px',
                        fontSize: '12px',
                        fontWeight: 500,
                        backgroundColor: `${getRoleColor(user.role)}20`,
                        color: getRoleColor(user.role)
                      }}>
                        {getRoleName(user.role)}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingUser?.id === user.id ? (
                      <select
                        value={editForm.status}
                        onChange={(e) => setEditForm({ ...editForm, status: e.target.value })}
                        className="edit-select"
                      >
                        <option value="active">Активен</option>
                        <option value="inactive">Заблокирован</option>
                      </select>
                    ) : (
                      <span
                        className={`status-badge ${user.status}`}
                        style={{ cursor: 'pointer' }}
                        onClick={() => toggleUserStatus(user.id, user.status, user.fullName || user.username)}
                        title="Нажмите для изменения статуса"
                      >
                        {user.status === 'active' ? '🟢 Активен' : '🔴 Заблокирован'}
                      </span>
                    )}
                  </td>
                  <td>
                    {editingUser?.id === user.id ? (
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button className="icon-btn success" onClick={updateUser} title="Сохранить">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </button>
                        <button className="icon-btn cancel" onClick={() => setEditingUser(null)} title="Отмена">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <line x1="18" y1="6" x2="6" y2="18" />
                            <line x1="6" y1="6" x2="18" y2="18" />
                          </svg>
                        </button>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button className="icon-btn" onClick={() => startEditing(user)} title="Редактировать">
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M17 3l4 4-7 7H10v-4l7-7z" />
                            <path d="M3 21h18" />
                          </svg>
                        </button>
                        <button 
                          className="icon-btn" 
                          onClick={() => resetUserPassword(user.id, user.fullName || user.username)} 
                          title="Сбросить пароль"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                            <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                          </svg>
                        </button>
                        <button 
                          className="icon-btn" 
                          onClick={() => toggleUserStatus(user.id, user.status, user.fullName || user.username)} 
                          title={user.status === 'active' ? 'Заблокировать' : 'Разблокировать'}
                        >
                          {user.status === 'active' ? (
                            // Заблокировать - круг с перечеркиванием (запрет)
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <circle cx="12" cy="12" r="10" />
                              <line x1="4.93" y1="4.93" x2="19.07" y2="19.07" />
                            </svg>
                          ) : (
                            // Разблокировать - открытый замок
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                              <path d="M7 11V7a5 5 0 0 1 9.9-1" />
                            </svg>
                          )}
                        </button>
                        <button 
                          className="icon-btn delete" 
                          onClick={() => deleteUser(user.id, user.fullName || user.username)} 
                          title="Удалить"
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6" />
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0h10" />
                            <line x1="10" y1="11" x2="10" y2="17" />
                            <line x1="14" y1="11" x2="14" y2="17" />
                          </svg>
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        ) : (
          <EmptyState
            icon={
              <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                <circle cx="12" cy="7" r="4" />
              </svg>
            }
            title="Пользователи не найдены"
            subtitle="Измените параметры поиска или создайте нового пользователя"
          />
        )}
      </div>

      {/* Статистика */}
      <div style={{ marginTop: '20px', padding: '16px', background: '#f9fafb', borderRadius: '12px', display: 'flex', gap: '24px', flexWrap: 'wrap' }}>
        <div><span style={{ color: '#6b7280' }}>Всего:</span> <strong>{stats.total}</strong></div>
        <div><span style={{ color: '#6b7280' }}>Активных:</span> <strong style={{ color: '#10b981' }}>{stats.active}</strong></div>
        <div><span style={{ color: '#6b7280' }}>Заблокированных:</span> <strong style={{ color: '#ef4444' }}>{stats.inactive}</strong></div>
        <div><span style={{ color: '#6b7280' }}>Администраторов:</span> <strong>{stats.admins}</strong></div>
        <div><span style={{ color: '#6b7280' }}>HR-менеджеров:</span> <strong>{stats.hrManagers}</strong></div>
        <div><span style={{ color: '#6b7280' }}>Кураторов:</span> <strong>{stats.curators}</strong></div>
      </div>

      {loading && <div style={{ textAlign: 'center', padding: '10px' }}>Обновление...</div>}
    </div>
  );
};

export default UsersTab;